import { head } from 'framer-motion/client';
import { generateSectionId } from '../../../HelpCenterComponents/ArticleLayout';

export const PrecticesTroubleshootingContentBody = ({ activeLeft }) => {
  const contentData = {
    0: {
      sections: [
        {
          type: 'text',
          heading: 'Best Practices and Troubleshooting',
          paragraphs: [
            'Developing Immersive experiences requires focusing on intuitive interactions, performance optimization, and user safety to create immersive yet practical applications. For the use cases in various industries like GeoSpatial, Training, Infrastructure, Tourism, Education, Highways etc. the following best practices are created from industry standards and prior explorations in immersive technologies like AR, VR, MR and XR.',
            'Integral parts of the development of immersive experiences include optimizing 3D assets for different devices which ensures smooth real-time rendering on limited hardware that is crucial for applications like geospatial overlays or training simulations in applicable workflows. Key techniques focus on reducing polygon counts, texture sizes, and draw calls while preserving visual fidelity.',
          ],
        },
      ],
    },
    1: {
      sections: [
        {
          type: 'text',
          heading: 'Best Practices',
          paragraphs: [],
        },
        {
          type: 'list',
          items: [
            {
              title: 'Planning',
              description:
                'Define clear goals, target audience, and KPIs early, such as reducing training errors in highway simulations by 20-30%. Conduct market research and competitor analysis to identify unique features, like geospatial overlays for site inspections. Choose platforms like Unity with AR Foundation for cross-platform development, aligning with the use of tools like ARKit/ARCore in constructing immersive experiences.',
            },
            {
              title: 'UX Design Principles',
              description:
                'Prioritize intuitive interactions with natural gestures, voice commands, and direct manipulation to minimize cognitive load, as in AR overlays for UAV feeds or crew training. Maintain visual hierarchy, clear space, and onboarding tutorials that adapt to contexts like outdoor highway sites, providing non-intrusive help. Incorporate spatial awareness with accurate anchoring, shadows, and occlusion for realism in dynamic environments.',
            },
            {
              title: 'Performance Optimization',
              description:
                'Optimize for low latency and stable frame rates using LOD rendering, texture compression, and efficient tracking like SLAM/GNSS hybrids for geospatial accuracy. Test across devices for battery drain and heat, ensuring seamless rendering in field scenarios like AR measurement validation. Leverage cloud backends for 3D assets to handle large models in digital twins or BIM integrations.',
            },
            {
              title: 'Safety and Accessibility',
              description:
                'Design for user safety by avoiding distractions in mobile contexts, adding peripheral alerts for hazards in training sims. Ensure accessibility with scalable UI, audio cues, and haptic feedback; respect privacy in data-heavy apps like remote sensing. Validate calibration procedures routinely, reporting uncertainty for compliance in industrial uses.',
            },
            {
              title: 'Prototype & Iteration',
              description:
                'Prototype early with wireframes, then usability test in real environments like construction sites for feedback on immersion and accuracy. Measure success via KPIs like error reduction or engagement time, iterating based on metrics from ARCore guidelines. Pilot in targeted domains, such as 5-km highway stretches, before scaling.',
            },
            {
              title: 'Reduce Polygon Count',
              description:
                'Simplify meshes using decimation tools to eliminate unnecessary vertices, aiming for under 50k triangles per model for mobile AR, as an example. Retain detail on visible surfaces and use quad-based topology to avoid artifacts. Tools like Blenders Decimate modifier or Unitys Level of Details (LOD) system help balance performance and quality.',
            },
            {
              title: 'Optimize Textures and Materials',
              description:
                'Resize textures to power-of-two dimensions (e.g., 1024x1024 max) and use compression formats like ASTC or ETC2. Create atlases to combine multiple textures, reducing memory use and draw calls. Merge similar materials and bake lighting/shadows to avoid runtime calculations.',
            },
            {
              title: 'Implement LOD & Batching',
              description:
                'Simplify meshes Use Level of Detail (LOD) groups with simpler meshes for distant objects, switching dynamically based on screen size. Enable GPU instancing and static/dynamic batching in Unity for repeated assets like highway elements.',
            },
            {
              title: 'File Format & Export Tips',
              description:
                'Export in efficient formats like GLB/GLTF or USDZ for embedded textures and compression. Keep total file size under 5-10MB; clean up hidden geometry and non-manifold edges pre-export.',
            },
          ],
        },
      ],
    },
    2: {
      sections: [
        {
          type: 'text',
          heading: 'Pitfalls to Avoid',
          paragraphs: [
            'Immersive experiences often suffer from performance bottlenecks due to real-time demands on mobile hardware, but these can be mitigated with targeted optimizations. Common pitfalls include excessive processing loads and poor rendering, especially relevant for your geospatial and training applications like highway AR overlays.',
          ],
        },
        {
          type: 'list',
          items: [
            {
              title: 'High Processing Overhead',
              description:
                'Immersive experience requires simultaneous camera processing, SLAM tracking, and 3D rendering, straining CPUs/GPUs and causing frame drops or latency on mid-tier devices. Thermal throttling and battery drain worsen during prolonged use in field scenarios. Avoid by offloading to neural engines, minimizing background tasks, and testing across device ranges.',
            },
            {
              title: 'Rendering Complexity',
              description:
                'High-polygon models, dynamic lighting, shadows, and inaccurate occlusion overload GPUs, leading to stuttering or visual glitches. Feature overload with unnecessary animations exacerbates this in complex apps like BIM visualizations. Counter with LOD for distant objects, baked lighting, texture atlasing, and occlusion culling to cut draw calls.',
            },
            {
              title: 'Tracking & Latency Issues',
              description:
                'Sensor noise from poor lighting, glossy surfaces, or featureless environments causes inaccurate anchoring and drift, breaking immersion. Latency between motion and updates creates disconnects in dynamic uses like UAV feeds. Mitigate via robust calibration, predictive algorithms, ML for ambiguous scenes, and adaptive lighting adjustments.',
            },
            {
              title: 'Hardware & Environment Mismatch',
              description:
                'Ignoring varied lighting/spatial constraints or older devices leads to failures in real-world outdoor settings like construction sites. Overlooking battery/heat limits shortens sessions. Test extensively in diverse conditions, use dynamic quality scaling, and prioritize core features over bloat.',
            },
          ],
        },
      ],
    },
  };

  const currentContent = contentData[activeLeft] || contentData[0];

  return (
    <div className="text-[#FFFFFF]">
      {currentContent?.intro && (
        <p className="font-light font-jakarta text-[15px] leading-relaxed mb-8">
          {currentContent.intro}
        </p>
      )}

      {currentContent?.sections?.map((section, index) => {
        const sectionId = section?.heading
          ? generateSectionId(section.heading)
          : null;

        return (
          <div key={index} className="mb-10">
            {/* Heading */}
            {section?.heading && (
              <h2
                id={sectionId}
                className="text-[22px] font-medium text-[#D8D8D8] mb-4 scroll-mt-28"
              >
                {section.heading}
              </h2>
            )}

            {/* Paragraphs */}
            {section?.paragraphs?.map((paragraph, pIndex) => (
              <p
                key={pIndex}
                className="font-light text-[15px] leading-relaxed mb-4 text-[#E4E4E4]"
              >
                {paragraph}
              </p>
            ))}
            {/* LIST SECTION */}
            {section?.type === 'list' && section?.items && (
              <ul className="list-disc list-inside space-y-4 text-[15px] text-[#E4E4E4]">
                {section.items.map((item, i) => (
                  <li key={i} className="font-light">
                    <span className="font-semibold text-white">
                      {item?.title}
                    </span>
                    {item?.description && (
                      <span className="font-light"> – {item.description}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};
